import {
  type PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import type { Vendor } from '../../types/shared';
import type { RootState } from '../index';
import {
  bulkImportVendors,
  createVendor,
  deleteVendor,
  fetchVendorById,
  fetchVendors,
  updateVendor,
} from '../thunks/vendorThunks';

// Entity adapter for normalized state
const vendorsAdapter = createEntityAdapter<Vendor>({
  selectId: (vendor) => vendor.id,
  sortComparer: (a, b) => (b.rating || 0) - (a.rating || 0),
});

// Re-export thunks
export {
  fetchVendors,
  fetchVendorById,
  createVendor,
  updateVendor,
  deleteVendor,
  bulkImportVendors,
};

// Slice
const vendorsSlice = createSlice({
  name: 'vendors',
  initialState: vendorsAdapter.getInitialState({
    loading: 'idle' as 'idle' | 'pending' | 'succeeded' | 'failed',
    error: null as string | null,
    selectedCategory: 'all',
    searchQuery: '',
  }),
  reducers: {
    setSelectedCategory: (state, action: PayloadAction<string>) => {
      state.selectedCategory = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    clearFilters: (state) => {
      state.selectedCategory = 'all';
      state.searchQuery = '';
    },
    vendorAdded: vendorsAdapter.addOne,
    vendorUpdated: vendorsAdapter.updateOne,
    vendorRemoved: vendorsAdapter.removeOne,
  },
  extraReducers: (builder) => {
    builder
      // Fetch vendors
      .addCase(fetchVendors.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        vendorsAdapter.setAll(state, action.payload);
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.error.message || 'Failed to fetch vendors';
      })
      // Fetch vendor by ID
      .addCase(fetchVendorById.fulfilled, (state, action) => {
        vendorsAdapter.upsertOne(state, action.payload);
      })
      // Create vendor
      .addCase(createVendor.fulfilled, (state, action) => {
        vendorsAdapter.addOne(state, action.payload);
      })
      // Update vendor
      .addCase(updateVendor.fulfilled, (state, action) => {
        vendorsAdapter.updateOne(state, {
          id: action.payload.id,
          changes: action.payload,
        });
      })
      // Delete vendor
      .addCase(deleteVendor.fulfilled, (state, action) => {
        vendorsAdapter.removeOne(state, action.payload);
      })
      // Bulk import
      .addCase(bulkImportVendors.fulfilled, (state, action) => {
        vendorsAdapter.upsertMany(state, action.payload);
      });
  },
});

// Export actions
export const {
  setSelectedCategory,
  setSearchQuery,
  clearFilters,
  vendorAdded,
  vendorUpdated,
  vendorRemoved,
} = vendorsSlice.actions;

// Export selectors
export const {
  selectAll: selectAllVendors,
  selectById: selectVendorById,
  selectIds: selectVendorIds,
  selectEntities: selectVendorEntities,
  selectTotal: selectTotalVendors,
} = vendorsAdapter.getSelectors((state: RootState) => state.vendors);

// Custom selectors
export const selectVendorsLoading = (state: RootState) => state.vendors.loading;
export const selectVendorsError = (state: RootState) => state.vendors.error;
export const selectSelectedCategory = (state: RootState) => state.vendors.selectedCategory;
export const selectSearchQuery = (state: RootState) => state.vendors.searchQuery;

// Filtered vendors selector
export const selectFilteredVendors = createSelector(
  [selectAllVendors, selectSelectedCategory, selectSearchQuery],
  (vendors, category, searchQuery) => {
    let filtered = vendors;

    if (category !== 'all') {
      filtered = filtered.filter((v) => v.category === category);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.name.toLowerCase().includes(query) ||
          v.category.toLowerCase().includes(query) ||
          v.services.some((s) => s.toLowerCase().includes(query))
      );
    }

    return filtered;
  }
);

// Vendor categories selector
export const selectVendorCategories = createSelector([selectAllVendors], (vendors) => {
  const categories = new Set(vendors.map((v) => v.category));
  return ['all', ...Array.from(categories)];
});

// Top rated vendors selector
export const selectTopRatedVendors = createSelector([selectAllVendors], (vendors) =>
  vendors.filter((v) => v.rating >= 4.5).slice(0, 10)
);

export default vendorsSlice.reducer;
